import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const SESSION_KEY = "rtc_session_id";
const VARIANT_KEY = "rtc_checklist_variant";

export const CHECKLIST_VARIANTS = {
  A: { id: "A", cta: "Send Me the Checklist", heading: "Send me the checklist." },
  B: {
    id: "B",
    cta: "Get My Free IRA Tax Audit",
    heading: "Get my free IRA tax audit.",
  },
};

// Single, dev-only logger so every telemetry failure goes through one place.
// In production builds the bodies are never executed — keeping our bundle
// free of console output and giving us one site to swap for a real
// monitoring sink (Sentry / LogRocket) later if we want.
const IS_DEV = process.env.NODE_ENV !== "production";
function devWarn(label, err) {
  if (!IS_DEV) return;
  // eslint-disable-next-line no-console
  console.warn(`[analytics] ${label}:`, err?.message || err);
}

function makeId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

// Session-scoped, non-PII anonymous IDs.
//
// SECURITY NOTE: The values stored here are random UUIDs used purely to
// group anonymous, fire-and-forget page-view events on the backend. They are
// NOT authentication tokens, NOT identifiers tied to a real user, and contain
// no PII. We deliberately use sessionStorage (not localStorage) so the values
// do not persist beyond the browser tab and are wiped on close. Because the
// payload is non-sensitive and never read back into a privileged context,
// httpOnly cookies are intentionally not used — they would require server-set
// session state for a purely client-side telemetry concern.
function safeSessionGet(key) {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch (err) {
    devWarn("sessionStorage read failed", err);
    return null;
  }
}

function safeSessionSet(key, value) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, value);
  } catch (err) {
    // Safari private mode / quota exceeded — analytics IDs are non-critical
    // so we drop the value and continue.
    devWarn("sessionStorage write failed", err);
  }
}

export function getSessionId() {
  let id = safeSessionGet(SESSION_KEY);
  if (!id) {
    id = makeId();
    safeSessionSet(SESSION_KEY, id);
  }
  return id;
}

export function getChecklistVariant() {
  let v = safeSessionGet(VARIANT_KEY);
  if (v !== "A" && v !== "B") {
    v = Math.random() < 0.5 ? "A" : "B";
    safeSessionSet(VARIANT_KEY, v);
  }
  return CHECKLIST_VARIANTS[v] || CHECKLIST_VARIANTS.A;
}

export function trackEvent(event, opts = {}) {
  // Fire-and-forget. Never block the UI on analytics failures.
  try {
    const payload = {
      event,
      variant: opts.variant ?? null,
      path:
        typeof window !== "undefined"
          ? window.location.pathname + window.location.hash
          : null,
      session_id: getSessionId(),
      meta: opts.meta ?? null,
    };
    axios.post(`${API}/events`, payload).catch((err) => devWarn("event post failed", err));
  } catch (err) {
    devWarn("trackEvent threw", err);
  }
}
